// Libraries
import dayjs from 'dayjs';
import PropTypes from 'prop-types';
import { useContext, useState } from 'react';
import { useSelector } from 'react-redux';
import { useSearchParams } from 'react-router';
import styled from 'styled-components';

// Dependencies
import Customization from 'models/Customization';
import TranslationsContext from 'providers/translationsProvider';
import { useListCustomizationsQuery } from 'services/apiSlices/customizationApi';
import { selectors as langSelectors } from 'store/modules/translations';
import { customization as customizationKey } from 'utils/localStorage';
import queryString from 'utils/queryString';
import useActivityTracker from 'utils/useActivityTracker';

// Services
import teamService from 'services/team';

// Components
import { Col, Row, Flex } from 'antd';
import { CardLink, Img } from 'components/elements';
import { P } from 'components/fontSystem';
import RegisterForm from 'containers/RegisterForm';
import Layout from 'views/Auth/Layout';
import EmailModal from './EmailModal';

// Assets
import whiteLogo from 'static/images/Bridgeforbillions_white.png';
import { getCustomizationProps, getLayoutStyle } from 'views/Auth/utils';

const STATUS_INVITE_ACCEPTED = 1;

const year = dayjs().year();

const Signup = ({ isCustomLink }) => {
  const { format, t } = useContext(TranslationsContext);

  const [showModal, setShowModal] = useState(false);
  const [userData, setUserData] = useState({});
  const [timerActive, setTimerActive] = useState(true);

  const selectedLanguage = useSelector(langSelectors.getSelectedLanguage);

  const [searchParams] = useSearchParams();
  const programId = searchParams.get('program') || localStorage.getItem(customizationKey);
  const { data: customization = new Customization(), isFetching } = useListCustomizationsQuery(
    { program: programId },
    {
      skip: !programId,
    }
  );

  const { memberToken, mentorToken } = queryString.parse(document.location.search);
  const handleOnAuth = ({ member, mentor }) => {
    if (memberToken || mentorToken) {
      teamService.updateMemberInvite({
        id: memberToken || mentorToken,
        member: member && member.id,
        mentor: mentor && mentor.id,
        status: STATUS_INVITE_ACCEPTED,
      });
    }

    if (customization?.program) {
      localStorage.setItem(customizationKey, customization.program);
    }
  };

  const layoutStyle = getLayoutStyle(customization, isFetching);
  const customizationProps = getCustomizationProps(customization, isFetching);

  const checkLanguage = () => {
    // This is used to customize the links to the knowledge base articles
    switch (selectedLanguage) {
      case 'es_es':
        return 'es/';
      case 'fr_fr':
        return 'fr/';
      case 'pt_br':
      case 'pt_pt':
        return 'pt-pt/';
      case 'it_it':
        return 'it/';
      default:
        return '';
    }
  };
  const LinkList = [
    {
      title: t('What is Bridge for Billions?'),
      link: `https://43666717.hs-sites.com/${checkLanguage()}knowledge-base/what-is-bridge-for-billions`,
    },
    {
      title: t('The benefits of joining a program'),
      link: `https://43666717.hs-sites.com/${checkLanguage()}knowledge-base/benefits-for-programs-participants`,
    },
    {
      title: t('Is this for me? Check the selection criteria'),
      link: `https://43666717.hs-sites.com/${checkLanguage()}knowledge-base/which-is-our-selection-criteria`,
    },
    {
      title: t('What do Bridge programs offer?'),
      link: `https://43666717.hs-sites.com/${checkLanguage()}knowledge-base/what-does-a-bridge-for-billions-program-consist-of`,
    },
    {
      title: t('Need help?'),
      link: 'mailto:support@bridgeforbillions.org?subject=I%20need%20help%20with%20my%20signup%20process',
    },
  ];

  const callbackTracker = () => {
    setShowModal(true);
  };

  useActivityTracker({
    intervalWait: 15000,
    trackerIsActive: timerActive,
    callback: callbackTracker,
  });

  const closeModal = () => {
    setTimerActive(false);
    setShowModal(false);
  };

  const alignUserData = data => {
    setUserData({
      ...data,
    });
  };

  if (isFetching) return null; // avoid background flashing during customization fetch

  return (
    <Layout style={layoutStyle}>
      <Layout.Header />
      <Layout.Content>
        <StyleRow align="middle" justify="center" type="flex">
          {!customization.id && (
            <Col xs={24} sm={24} md={12} lg={12} xl={12} xxl={12}>
              <InformationSection>
                <div className="video-container">
                  <iframe
                    style={{ border: 'none', borderRadius: '4px' }}
                    src="https://www.youtube.com/embed/g9hR6KA8moA?si=LJiba4TzN0_ow3Y2"
                    title="Bridge for Billions - What is Bridge for Billions?"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                    allowFullScreen
                  />
                </div>
                {LinkList.map(element => (
                  <CardLink placeholder={element.title} href={element.link} key={element.link} />
                ))}
              </InformationSection>
            </Col>
          )}
          <Col xs={24} sm={24} md={12} lg={12} xl={12} xxl={12}>
            <FormBlock>
              <RegisterForm
                {...customizationProps}
                onAuth={handleOnAuth}
                isCustomLink={isCustomLink}
                alignUserData={alignUserData}
                disableMentor={Boolean(memberToken)}
                disableMember={Boolean(mentorToken)}
                customization={customization}
              />
            </FormBlock>
          </Col>
        </StyleRow>
      </Layout.Content>
      <Layout.Footer style={{ padding: '24px' }}>
        {customization.id && (
          <Flex justify="center" align="center" gap={5} vertical>
            <P
              $italic
              color="white"
              $mb0
              style={{
                fontSize: 12,
              }}
            >
              {t('This online incubation program is powered by')}
            </P>
            <Img width={157} src={whiteLogo} />
          </Flex>
        )}
        <P style={{ fontSize: 12, color: customization.id ? 'white' : 'black' }}>
          {format(t('Copyright © %(year)s. All Rights Reserved'), {
            year: String(year),
          })}
        </P>
      </Layout.Footer>
      <EmailModal userData={userData} open={showModal} onClose={closeModal} t={t} />
    </Layout>
  );
};

const StyleRow = styled(Row)`
  @media (max-width: 768px) {
    flex-direction: column-reverse;
  }
`;

const InformationSection = styled.div`
  max-width: 480px;
  margin: auto;

  .video-container {
    position: relative;
    padding-bottom: 56.25%;
  }

  .video-container iframe {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
  }

  @media (max-width: 768px) {
    .video-container iframe {
      margin: 0;
    }
  }
`;

const FormBlock = styled.div`
  max-width: 600px;
  overflow: hidden;
  border-radius: 4px;
  margin: auto;
  filter: drop-shadow(0px 2px 2px rgba(0, 0, 0, 0.2));

  ::-webkit-scrollbar-thumb {
    background: #f2f2f2;
    border-radius: 50px;
  }
  ::-webkit-scrollbar {
    width: 7px;
    border-radius: 50px;
  }
`;

Signup.propTypes = {
  isCustomLink: PropTypes.bool.isRequired,
};

export default Signup;
// Dynamicly imported
